/**
 * useAlertMatrix — Story 3.4 (Multi-Channel Alert-Matrix & Telegram-Connect UI)
 *
 * Kanonische Alert-Matrix (19 Event-Typen x 4 Kanaele, KEIN Discord):
 *   GET  /api/v2/notifications/matrix  -> { data: NotificationMatrix }
 *   PUT  /api/v2/notifications/matrix  -> Bulk-Upsert je (event_type, channel)
 *
 * Traffic-Schwellenwert (Story 3.3, User-Feld traffic_drop_threshold):
 *   PATCH /api/v2/account/notification_settings { traffic_drop_threshold_pct }
 *   (der Wert kommt lesend aus matrix.traffic_drop_threshold)
 *
 * Contract-Luecke (dokumentiert statt erfunden): Ein Notification-LOG-Endpunkt
 * (gemaess Design-Referenz notification-log.html) existiert NICHT in
 * api-types/openapi.yaml — die Log-Ansicht (/notifications) bleibt daher der
 * bestehende Stub aus W4.6 und wird hier bewusst nicht angebunden.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { components, operations } from '@heliumtracker/api-types'
import apiClient from '../../lib/api-client'

export type AlertMatrix = components['schemas']['NotificationMatrix']
export type AlertMatrixEvent = components['schemas']['NotificationMatrixEvent']
export type AlertChannelStatus = components['schemas']['NotificationChannelStatus']

type PutBody =
  operations['putApiV2NotificationsMatrix']['requestBody']['content']['application/json']
export type AlertPreference = PutBody['preferences'][number]
export type AlertEventType = AlertPreference['event_type']
export type AlertChannel = AlertPreference['channel']

/** Spaltenreihenfolge gemaess Design-Referenz settings.html (Notifications-Tab). */
export const ALERT_CHANNEL_ORDER: readonly AlertChannel[] = ['email', 'push', 'webhook', 'telegram']

export const ALERT_MATRIX_QUERY_KEY = ['notifications', 'matrix'] as const
/** Legacy-Matrix-Key (Paritaets-Runde 3) — der Threshold-PATCH aktualisiert beide. */
const LEGACY_MATRIX_QUERY_KEY = ['account', 'notification_settings'] as const

async function fetchAlertMatrix(): Promise<AlertMatrix | null> {
  const result = await apiClient.GET('/api/v2/notifications/matrix')
  if (result.error) {
    throw new Error('Failed to load alert matrix')
  }
  return result.data?.data ?? null
}

async function putAlertMatrix(preferences: AlertPreference[]): Promise<AlertMatrix | null> {
  const result = await apiClient.PUT('/api/v2/notifications/matrix', {
    body: { preferences },
  })
  if (result.error) {
    throw new Error('Failed to save alert matrix')
  }
  return result.data?.data ?? null
}

async function patchTrafficThreshold(pct: number): Promise<void> {
  // Contract-Quirk: der PATCH-Body ist als
  //   { traffic_drop_threshold_pct?: number } & { [key: string]: boolean }
  // generiert — die Number-Property kollidiert mit der Boolean-Index-Signatur,
  // daher der gezielte Cast auf den generierten Body-Typ.
  type PatchBody =
    operations['updateApiV2AccountNotificationSettings']['requestBody']['content']['application/json']
  const body = { traffic_drop_threshold_pct: pct } as unknown as PatchBody
  const result = await apiClient.PATCH('/api/v2/account/notification_settings', { body })
  if (result.error) {
    throw new Error('Failed to save traffic threshold')
  }
}

export function useAlertMatrix() {
  const queryClient = useQueryClient()

  const query = useQuery<AlertMatrix | null>({
    queryKey: ALERT_MATRIX_QUERY_KEY,
    queryFn: fetchAlertMatrix,
    retry: false,
    staleTime: 60_000,
  })

  const saveMutation = useMutation({
    mutationFn: (preferences: AlertPreference[]) => putAlertMatrix(preferences),
    onSuccess: (updated) => {
      if (updated) {
        queryClient.setQueryData(ALERT_MATRIX_QUERY_KEY, updated)
      } else {
        queryClient.invalidateQueries({ queryKey: ALERT_MATRIX_QUERY_KEY })
      }
    },
  })

  const thresholdMutation = useMutation({
    mutationFn: (pct: number) => patchTrafficThreshold(pct),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERT_MATRIX_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: LEGACY_MATRIX_QUERY_KEY })
    },
  })

  return {
    matrix: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
    saveMatrix: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    saveThreshold: thresholdMutation.mutateAsync,
    isSavingThreshold: thresholdMutation.isPending,
  }
}
